import { FC, useState, useEffect } from 'react';
import ToolConditionItem from '../ToolConditionItem/ToolConditionItem';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import DownloadToolInformation from '../DownloadToolInformation/DownloadToolInformation';
import { IInspectionField } from '../../toolInfoTab.types';
import './ToolConditionsPanel.scss';

/**
 * Panel que muestra todas las condiciones físicas de la herramienta
 * @param fields - Campos de inspección actuales de la herramienta
 * @param onFieldsChange - Función que se ejecuta cuando cambian los campos
 * @param disabled - Si el panel está deshabilitado
 * @param productId - ID del producto para la descarga
 * @param disableDownload - Si el botón de descarga está deshabilitado
 */
const ToolConditionsPanel: FC<ToolConditionsPanelProps> = ({
  fields,
  onFieldsChange,
  disabled,
  productId,
  isWarehouseLocation,
  disableDownload
}) => {
  const [localFields, setLocalFields] = useState<IInspectionField[]>(fields);

  const booleanFields = localFields?.filter(field => field?.dataType === 'boolean');
  const languageField = localFields?.find(
    field => field?.dataType === 'string' && field?.name?.toLowerCase()?.includes('idioma')
  );
  const renderMessage = isWarehouseLocation
    ? 'Al modificar la información del equipo, los cambios se reflejan en WMS.'
    : 'Este equipo no está en bodega, por lo que no es posible actualizar su información.';

  useEffect(() => {
    setLocalFields(fields);
  }, [fields]);

  const handleFieldChange = (fieldId: number, newValue: string) => {
    const updatedFields = localFields?.map(field => (field.id === fieldId ? { ...field, value: newValue } : field));
    setLocalFields(updatedFields);
    onFieldsChange(updatedFields);
  };

  return (
    <div className="toolConditionsPanel">
      <div className="message">
        <span>{renderMessage}</span>
      </div>

      <div className="conditionsList">
        {booleanFields?.map((field, index) => (
          <ToolConditionItem
            key={field?.id}
            label={field?.name}
            value={field?.value === 'true'}
            onChange={value => handleFieldChange(field?.id, value?.toString())}
            disabled={disabled}
            isFirst={index === 0}
          />
        ))}

        {languageField && (
          <ToolConditionItem
            label={languageField.name}
            customStyles={{ padding: '0px' }}
            customControl={
              <LanguageSelector
                value={languageField?.value || ''}
                onChange={value => handleFieldChange(languageField?.id, value)}
                disabled={disabled}
              />
            }
          />
        )}

        <div className="downloadToolInfoContainer">
          <DownloadToolInformation productId={productId} disabled={disableDownload} />
        </div>
      </div>
    </div>
  );
};

interface ToolConditionsPanelProps {
  fields: IInspectionField[];
  onFieldsChange: (fields: IInspectionField[]) => void;
  disabled?: boolean;
  productId?: number;
  isWarehouseLocation?: boolean;
  disableDownload?: boolean;
}

export default ToolConditionsPanel;
